import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from "react-helmet-async";
import AddFundsModal from "../margin/AddFundsModal";
import { FuturesTradeTransferProvider, useFuturesTradeTransfer } from "../futures_trade/FuturesTradeTransferContext";
import StakingService from "../../../api/services/StakingService";
import { normalizeIconPathString } from "../WithdrawPage/withdrawPageHelpers";
import Footer from "../../../customComponents/Footer";
import './staking.css';

const getPackageIcon = (pkg) => {
    const fromApi = normalizeIconPathString(pkg?.iconPath);
    if (fromApi) return fromApi;
    const currency = String(pkg?.currency || "").toLowerCase();
    return currency ? `/images/staking_img/${currency}_icon.svg` : "/images/staking_img/usdt_icon.svg";
};

const getCoinIconPath = (symbol) => {
    const sym = String(symbol || "").toLowerCase();
    return `/images/staking_img/${sym}_icon.svg`;
};

const MOCK_ONGOING_PROJECTS = [
    {
        id: 0,
        logo: 'USDT',
        name: 'USDT Soft Staking',
        status: 'Ongoing',
        badge: 'Flexible',
        minAmount: '10 USDT',
        participants: '12,543',
        eventTime: 'Daily Snapshot: 00:00 UTC',
        pools: [
            {
                name: 'Hold USDT to Earn',
                coinIcon: 'USDT',
                allocation: '6.5%',
                allocationCoin: 'Est. APR',
                commitment: '1,452,240',
                commitmentCoin: 'USDT',
                cap: '100,000',
                capCoin: 'USDT',
                subPrice: 'Flexible Staking',
                poolParticipants: '9,543'
            },
            {
                name: 'Hold GUSD to Earn',
                coinIcon: 'GUSD',
                allocation: '8.0%',
                allocationCoin: 'Est. APR',
                commitment: '645,120',
                commitmentCoin: 'GUSD',
                cap: '50,000',
                capCoin: 'GUSD',
                subPrice: 'Flexible Staking',
                poolParticipants: '3,000'
            }
        ]
    },
    {
        id: 1,
        logo: 'BTC',
        name: 'BTC Soft Staking',
        status: 'Ongoing',
        badge: 'Flexible',
        minAmount: '0.001 BTC',
        participants: '8,432',
        eventTime: 'Daily Snapshot: 00:00 UTC',
        pools: [
            {
                name: 'Hold BTC to Earn',
                coinIcon: 'BTC',
                allocation: '3.8%',
                allocationCoin: 'Est. APR',
                commitment: '14.52',
                commitmentCoin: 'BTC',
                cap: '5',
                capCoin: 'BTC',
                subPrice: 'Flexible Staking',
                poolParticipants: '8,432'
            }
        ]
    }
];

const SoftStaking = () => {
    const navigate = useNavigate();
    const { openWalletTransfer } = useFuturesTradeTransfer() || {};

    const [openFaq, setOpenFaq] = useState(null);
    const [addFundsOpen, setAddFundsOpen] = useState(false);
    const [softStakingEnabled, setSoftStakingEnabled] = useState(false);
    const [showSoftStakingModal, setShowSoftStakingModal] = useState(false);
    const [softStakingActionLoading, setSoftStakingActionLoading] = useState(false);
    const [softStakingError, setSoftStakingError] = useState("");
    const [softStakingSuccess, setSoftStakingSuccess] = useState(null); // { active: boolean, message: string } | null
    const [packages, setPackages] = useState([]);
    const [packagesLoading, setPackagesLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
    const [activeTab, setActiveTab] = useState("All");
    const pageLimit = 10;

    const fetchPackages = useCallback(async (page = 1) => {
        setPackagesLoading(true);
        const res = await StakingService.getSoftStakingPackages(page, pageLimit);
        if (res?.success && Array.isArray(res.data)) {
            setPackages(res.data);
            if (res.pagination) {
                setPagination((prev) => ({ ...prev, ...res.pagination, page }));
            }
        } else {
            setPackages([]);
        }
        setPackagesLoading(false);
    }, []);

    useEffect(() => {
        fetchPackages(1);
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        if (token) {
            StakingService.getSoftStakingStatus().then((res) => {
                if (res?.success) {
                    setSoftStakingEnabled(res.data?.softStakingStaus === true);
                }
            });
        }
    }, [fetchPackages]);

    useEffect(() => {
        const anyModalOpen = addFundsOpen || showSoftStakingModal || Boolean(softStakingSuccess);
        if (anyModalOpen) {
            document.body.style.overflow = "hidden";
            document.documentElement.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
            document.documentElement.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
            document.documentElement.style.overflow = "";
        };
    }, [addFundsOpen, showSoftStakingModal, softStakingSuccess]);

    const openSoftStakingModal = () => {
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        if (!token) {
            navigate("/login");
            return;
        }
        setSoftStakingError("");
        setShowSoftStakingModal(true);
    };

    const handleToggleSoftStaking = async () => {
        const nextActive = !softStakingEnabled;
        setSoftStakingActionLoading(true);
        setSoftStakingError("");
        try {
            const res = await StakingService.setSoftStakingStatus(nextActive);
            if (res?.success) {
                const status = typeof res.data?.softStakingStaus === "boolean" ? res.data.softStakingStaus : nextActive;
                setSoftStakingEnabled(status);
                setShowSoftStakingModal(false);
                setSoftStakingSuccess({
                    active: status,
                    message: res.message || (status ? "Soft staking activated successfully" : "Soft staking deactivated successfully"),
                });
            } else {
                setSoftStakingError(res?.message || "Something went wrong. Please try again.");
            }
        } catch {
            setSoftStakingError("Something went wrong. Please try again.");
        } finally {
            setSoftStakingActionLoading(false);
        }
    };

    const ongoingCount = MOCK_ONGOING_PROJECTS.length;

    const filteredPackages = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        let list = packages;

        if (activeTab === "Ongoing") {
            list = packages.filter(pkg => pkg.status !== "Ended" && pkg.status !== "ended" && pkg.status !== "inactive");
        }

        if (!q) return list;
        return list.filter((pkg) => {
            const currency = String(pkg?.currency || "").toLowerCase();
            const fullName = String(pkg?.currencyFullName || "").toLowerCase();
            return currency.includes(q) || fullName.includes(q);
        });
    }, [packages, searchQuery, activeTab]);

    const toggleFaq = (i) => setOpenFaq((v) => (v === i ? null : i));

    const STAKING_FAQ_ITEMS = [
        {
            question: "What is Soft Staking?",
            answer: "AGCX Soft Staking lets you earn passive rewards on eligible crypto in your AGCX wallet while keeping full access to those assets. Unlike locked staking, funds stay flexible and can be used or withdrawn according to product rules."
        },
        {
            question: "How does Soft Staking work?",
            answer: "When eligible assets sit in your AGCX account and Soft Staking is enabled, they can earn rewards based on holdings. Rewards follow the AGCX Soft Staking schedule shown on the product page."
        },
        {
            question: "Do I need to lock my assets?",
            answer: "No. AGCX Soft Staking does not require a lock-up period. You can access, trade, or withdraw eligible assets on AGCX without a separate unstaking wait, subject to product rules."
        },
        {
            question: "Which cryptocurrencies are supported?",
            answer: "Eligible assets are listed on the AGCX Soft Staking dashboard and can change over time. Always check the live list on AGCX before you rely on rewards."
        },
        {
            question: "How are staking rewards calculated?",
            answer: "AGCX typically calculates rewards from your average daily balance of eligible assets and the current APY shown for each coin on Soft Staking."
        },
        {
            question: "When will I receive my rewards?",
            answer: "AGCX credits Soft Staking rewards on a periodic schedule that can differ by asset. Check your AGCX staking or wallet history for the latest payouts."
        },
        {
            question: "Is there a minimum amount required to participate?",
            answer: "Minimums depend on the asset and are shown on AGCX Soft Staking. Some coins allow small balances; others need a minimum holding."
        },
        {
            question: "Can I trade my assets while earning rewards?",
            answer: "Yes. A main benefit of AGCX Soft Staking is flexibility. Eligible assets can usually be traded on AGCX while they remain in the program, as described on the product page."
        },
        {
            question: "Are staking rewards guaranteed?",
            answer: "No. AGCX Soft Staking rates can change with network conditions and market factors. Past returns do not guarantee future rewards."
        },
        {
            question: "Is Soft Staking secure?",
            answer: "AGCX Soft Staking is designed as a convenient way to earn on assets already on the platform. Crypto still involves market risk. Enable 2FA and complete KYC. Contact support@arabglobal.ae if you need help."
        },
        {
            question: "Can I stop Soft Staking at any time?",
            answer: "Yes. Because assets are not locked, you can stop by turning off Soft Staking or moving eligible assets out of the participating AGCX wallet, subject to the product rules."
        },
        {
            question: "Why choose Soft Staking?",
            answer: "AGCX Soft Staking is a simple way to earn on idle balances while keeping liquidity and access on AGCX, without a long lock-up."
        }
    ];

    const whyStakeData = [
        { title: "No Lock-up Required", desc: "Simply hold assets in your spot wallet to earn. No staking action needed." },
        { title: "Full Liquidity", desc: "Your assets remain fully liquid and accessible at all times." },
        { title: "100% Proof of Reserve", desc: "Your assets are fully backed and verified on-chain for complete transparency." }
    ];

    return (

        <>

            <Helmet>
                <title>Soft Staking | Flexible, No Lock-Up Crypto Staking – AGCX </title>
                <meta
                    name="description"
                    content="Earn rewards on your crypto with AGCX Soft Staking — no lock-up period, withdraw anytime, and start earning instantly on eligible assets."
                />
                <meta name="keywords" content="soft staking crypto, flexible staking, no lock-up staking " />
                <link rel="canonical" href="https://arabglobal.ae/soft-staking" />
            </Helmet>

            <div className="staking_page_wrapper">
                <div className="container">
                    <div className='staking_bnr_cnt d-flex'>
                        <div className='staking_bnr_left'>
                            <h1 className="staking_title">Soft Staking</h1>
                            <p className="staking_desc">Hold assets and earn rewards automatically</p>
                            <div className='staking_btn d-flex gap-2'>
                                {/* <button onClick={() => navigate('/staking-detail')}>About Soft Staking</button> */}
                                <button
                                    type="button"
                                    onClick={openSoftStakingModal}
                                // style={{ backgroundColor: "#1a1a1a", color: "white", display: 'flex', alignItems: 'center', gap: '8px' }}
                                >
                                    Start Earning
                                    {softStakingEnabled && (
                                        <span style={{
                                            fontSize: '11px', fontWeight: 600, color: '#03a66d',
                                            background: 'rgba(3,166,109,0.18)', borderRadius: '4px',
                                            padding: '2px 7px', lineHeight: '1.4',
                                        }}>
                                            Enabled
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>
                        <div className='staking_img'>
                            <img className='dark_img' src='/images/staking_img/staking_bnr_img.svg' alt='soft staking' />
                            <img className='light_img' src='/images/staking_img/staking_bnr_img_light.svg' alt='soft staking' />
                        </div>

                    </div>

                    <div className="staking_products_section">
                        <div className="staking_products_header softstakingsearch">
                            <div className="launchpad_projects_tabs" style={{ marginBottom: 0 }}>
                                <button
                                    type="button"
                                    className={`projects_tab ${activeTab === 'All' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('All')}
                                >
                                    All Products
                                </button>
                                {/* <button
                                type="button"
                                className={`projects_tab ${activeTab === 'Ongoing' ? 'active' : ''}`}
                                onClick={() => setActiveTab('Ongoing')}
                            >
                                Ongoing ({ongoingCount})
                            </button> */}
                            </div>
                            <div className="staking_products_filters">
                                <div className="searchBar custom-tabs">
                                    <i className="ri-search-2-line"></i>
                                    <input
                                        type="search"
                                        className="custom_search"
                                        placeholder="Search"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {activeTab === "All" ? (
                            <div className="staking_table_container">
                                <table className="staking_table">
                                    <thead>
                                        <tr>
                                            <th>Coin</th>
                                            <th>Min Holding</th>
                                            <th>Staking Cap</th>
                                            <th>Est. APR <span className="sort_icon">⬍</span></th>
                                            <th>Cumulative Rewards</th>
                                            <th>Type</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {packagesLoading ? (
                                            <tr>
                                                <td colSpan={6} style={{ textAlign: "center", padding: "32px" }}>
                                                    Loading packages...
                                                </td>
                                            </tr>
                                        ) : filteredPackages.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} style={{ textAlign: "center", padding: "32px" }}>
                                                    No soft staking packages available.
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredPackages.map((pkg) => {
                                                const coinIcon = getPackageIcon(pkg);
                                                return (
                                                    <tr key={pkg._id || pkg.packageId}>
                                                        <td>
                                                            <div className="coin_cell">
                                                                <div className="coin_icon_stak">
                                                                    <img
                                                                        src={coinIcon}
                                                                        alt={pkg.currency}
                                                                        onError={(e) => {
                                                                            e.target.onerror = null;
                                                                            e.target.src = `/images/staking_img/${String(pkg.currency || "usdt").toLowerCase()}_icon.svg`;
                                                                        }}
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <span className="coin_name">{pkg.currency}</span>
                                                                    {pkg.currencyFullName ? (
                                                                        <div style={{ fontSize: '12px', color: '#848e9c' }}>{pkg.currencyFullName}</div>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>{pkg.minAmount != null ? pkg.minAmount.toLocaleString() : '—'} {pkg.currency}</td>
                                                        <td>{pkg.stakingCap != null ? pkg.stakingCap.toLocaleString() : '—'} {pkg.currency}</td>
                                                        <td className="apr_cell" style={{ color: '#03a66d', fontWeight: 600 }}>
                                                            {pkg.returnPercentage != null ? `${pkg.returnPercentage}%` : '—'}
                                                        </td>
                                                        <td>{pkg.cumulativeRewards != null ? pkg.cumulativeRewards.toLocaleString() : '0'}</td>
                                                        <td className="type_cell">{pkg.type || '—'}</td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            /* Ongoing Card View (Launchpad Style) */
                            <div className="staking_cards_container" style={{ marginTop: '20px' }}>
                                {MOCK_ONGOING_PROJECTS.map((project) => (
                                    <div className="project_card" key={project.id}>
                                        <div className="project_card_status">{project.status}</div>
                                        <div className="project_card_inner">
                                            {/* Left Side Info */}
                                            <div className="project_card_left" onClick={() => navigate('/staking-detail')}>
                                                <div className="project_title_sec">
                                                    <div className="project_icon">
                                                        <img
                                                            src={getCoinIconPath(project.logo)}
                                                            alt={project.name}
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.src = "/images/staking_img/usdt_icon.svg";
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="project_name_badge">
                                                        <h3>{project.name}</h3>
                                                        {project.badge && <span className="initial_badge">{project.badge}</span>}
                                                    </div>
                                                </div>

                                                <div className="project_info_group">
                                                    <p className="info_label">Min Holding</p>
                                                    <div className="info_value">
                                                        <p>{project.minAmount}</p>
                                                    </div>
                                                </div>

                                                <div className="project_info_group">
                                                    <p className="info_label">Number of Participants</p>
                                                    <div className="info_value">
                                                        <p>{project.participants}</p>
                                                    </div>
                                                </div>

                                                <div className="project_info_group mt-auto">
                                                    <p className="info_label">Event Time</p>
                                                    <div className="info_value">
                                                        <p>{project.eventTime}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Right Side Pool Details */}
                                            <div className="project_card_right">
                                                {project.pools.map((pool, pIdx) => (
                                                    <div className="pool_card" key={pIdx} onClick={() => navigate('/staking-detail')}>
                                                        <div className="pool_title">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div className="pool_icon">
                                                                    <img
                                                                        src={getCoinIconPath(pool.coinIcon)}
                                                                        alt={pool.name}
                                                                        onError={(e) => {
                                                                            e.target.onerror = null;
                                                                            e.target.src = "/images/staking_img/usdt_icon.svg";
                                                                        }}
                                                                    />
                                                                </div>
                                                                <h4>{pool.name}</h4>
                                                            </div>
                                                        </div>

                                                        <div className="pool_stats_row">
                                                            <div className="pool_stat">
                                                                <p className="stat_label">Est. APR</p>
                                                                <p className="stat_value" style={{ color: '#03a66d', fontSize: '18px', fontWeight: 'bold' }}>
                                                                    {pool.allocation}
                                                                </p>
                                                            </div>
                                                            <div className="pool_stat text-end">
                                                                <p className="stat_label">Cumulative Rewards</p>
                                                                <p className="stat_value" style={{ fontSize: '15px' }}>
                                                                    {pool.commitment} <span>{pool.commitmentCoin}</span>
                                                                </p>
                                                            </div>
                                                        </div>

                                                        <div className="pool_stat mt-3">
                                                            <p className="stat_label">Staking Cap Limit</p>
                                                            <p className="stat_value">
                                                                {pool.cap} <span>{pool.capCoin}</span>
                                                            </p>
                                                        </div>

                                                        <div className="pool_summary mt-4">
                                                            <div className="summary_row">
                                                                <p className="summary_label">Staking Type</p>
                                                                <p className="summary_value">{pool.subPrice}</p>
                                                            </div>
                                                            <div className="summary_row">
                                                                <p className="summary_label">Number of Participants</p>
                                                                <p className="summary_value">{pool.poolParticipants}</p>
                                                            </div>
                                                        </div>

                                                        <button
                                                            className="trade_btn"
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const pair = pool.coinIcon === 'USDT' ? 'BTC_USDT' : `${pool.coinIcon}_USDT`;
                                                                navigate(`/trade/${pair}`);
                                                            }}
                                                        >
                                                            Trade
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {pagination.totalPages > 1 ? (
                            <div className="staking_pagination">
                                <button type="button" className="page_nav prev_nav" disabled={pagination.page <= 1} onClick={() => fetchPackages(pagination.page - 1)}>‹</button>
                                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((pageNum) => (
                                    <button key={pageNum} type="button" className={`page_num${pagination.page === pageNum ? " active" : ""}`} onClick={() => fetchPackages(pageNum)}>
                                        {pageNum}
                                    </button>
                                ))}
                                <button type="button" className="page_nav next_nav" disabled={pagination.page >= pagination.totalPages} onClick={() => fetchPackages(pagination.page + 1)}>›</button>
                            </div>
                        ) : null}
                    </div>

                    <section className="convert-page__faqSection mt-5" aria-labelledby="soft-staking-faq-title">
                        <h2 id="soft-staking-faq-title" className="convert-page__faqTitle">FAQ</h2>
                        <ul className="convert-page__faqList">
                            {STAKING_FAQ_ITEMS.map((item, i) => (
                                <li key={item.question} className="convert-page__faqItem">
                                    <button type="button" className="convert-page__faqBtn" onClick={() => toggleFaq(i)} aria-expanded={openFaq === i}>
                                        <span>{item.question}</span>
                                        <i className={`ri-arrow-down-s-line convert-page__faqChev${openFaq === i ? " convert-page__faqChev--open" : ""}`} aria-hidden />
                                    </button>
                                    {openFaq === i ? (
                                        <div className="convert-page__faqPanel" role="region"><p>{item.answer}</p></div>
                                    ) : null}
                                </li>
                            ))}
                        </ul>
                    </section>

                    {showSoftStakingModal && (
                        <div className="mint-modal-overlay" onClick={() => !softStakingActionLoading && setShowSoftStakingModal(false)}>
                            <div className="mint-modal-content ss-modal-content" onClick={(e) => e.stopPropagation()}>
                                <div className="mint-modal-header">
                                    <div className="mint-modal-title">
                                        <img src="/images/staking_img/usdt_icon.svg" alt="" className="mint-title-icon" />
                                        <span>Soft Staking</span>
                                    </div>
                                    <button
                                        type="button"
                                        className="mint-modal-close"
                                        disabled={softStakingActionLoading}
                                        onClick={() => setShowSoftStakingModal(false)}
                                    >
                                        <i className="ri-close-line" />
                                    </button>
                                </div>

                                <div className="mint-modal-body ss-modal-body">
                                    <div className="ss-status-row">
                                        <span className="ss-status-label">Current Status</span>
                                        <span className={`ss-status-badge ${softStakingEnabled ? 'ss-status-badge--enabled' : 'ss-status-badge--disabled'}`}>
                                            {softStakingEnabled ? "Enabled" : "Disabled"}
                                        </span>
                                    </div>

                                    <p className="ss-desc">
                                        {softStakingEnabled
                                            ? "Disabling soft staking will stop your eligible assets from earning rewards. You can re-enable it anytime."
                                            : "Enable soft staking to automatically earn rewards on your eligible holdings. Rewards become eligible from the next day (00:00 UTC)."}
                                    </p>

                                    {softStakingError && (
                                        <div className="ss-error">{softStakingError}</div>
                                    )}
                                </div>

                                <div className="mint-modal-footer">
                                    <div className="mint-actions">
                                        <button
                                            type="button"
                                            className="mint-btn-cancel"
                                            disabled={softStakingActionLoading}
                                            onClick={() => setShowSoftStakingModal(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className={`mint-btn-confirm ${softStakingEnabled ? 'ss-btn-disable' : ''}`}
                                            disabled={softStakingActionLoading}
                                            onClick={handleToggleSoftStaking}
                                        >
                                            {softStakingActionLoading
                                                ? "Processing..."
                                                : softStakingEnabled ? "Disable" : "Enable"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {softStakingSuccess && (
                        <div className="mint-modal-overlay" onClick={() => setSoftStakingSuccess(null)}>
                            <div className="mint-modal-content ss-success-modal" onClick={(e) => e.stopPropagation()}>
                                <div className="ss-success-content">
                                    <i
                                        className={`ri-checkbox-circle-fill ss-success-icon ${softStakingSuccess.active ? 'active' : 'inactive'}`}
                                        aria-hidden="true"
                                    />
                                    <h3 className="ss-success-title">
                                        {softStakingSuccess.active ? "Soft Staking Enabled" : "Soft Staking Disabled"}
                                    </h3>
                                    <p className="ss-success-desc">
                                        {softStakingSuccess.message}
                                    </p>
                                    <button
                                        type="button"
                                        className="mint-btn-confirm ss-success-btn"
                                        onClick={() => setSoftStakingSuccess(null)}
                                    >
                                        Done
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    <AddFundsModal open={addFundsOpen} onClose={() => setAddFundsOpen(false)} onTransferClick={openWalletTransfer} />
                </div>
            </div>

            <Footer />
        </>

    );
};

export default function SoftStakingWrapper() {
    return (
        <FuturesTradeTransferProvider>
            <SoftStaking />
        </FuturesTradeTransferProvider>
    );
}
